// src/components/documents/CustomerEmailChange.js — doced-8f3a61c2 (Document editor, Stage 5f-2)
//
// The inline "Change" box for the address an estimate is emailed to — used by the Review & send page and the
// estimate page's customer card. One email box (the same format rule as CC), Save / Cancel, and:
//   • "Also update <customer>'s record" — ticked by default, only for roles allowed to edit customers
//     (utils/estimateEmailChange.canUpdateCustomerRecord); others see "This changes the address on this
//     estimate only";
//   • "Other estimates and invoices keep the address they were made with."
// What Save does is the caller's (onSave(email, alsoRecord) → Promise<{ ok, message }>): the send page writes
// the saved estimate at once; the editor changes its unsaved estimate and the customer record.

import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { FIELD } from './fieldStyles';
import { emailProblem, plannedChanges, ESTIMATE_ONLY_NOTE, OTHERS_KEEP_NOTE } from '../../utils/estimateEmailChange';

export default function CustomerEmailChange({ idPrefix, current, recordEmail, customerName, canUpdateRecord, hasRecord, onSave, onCancel }) {
    const [value, setValue] = useState(current || '');
    const [alsoRecord, setAlsoRecord] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [tried, setTried] = useState(false);
    const inputRef = useRef(null);
    useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, []);

    const problem = emailProblem(value);
    const offerRecord = canUpdateRecord && hasRecord;
    // Save is on when it would change the estimate's address, or (ticked) the customer record's — never for nothing.
    const unchanged = !plannedChanges({ email: value, estimateEmail: current, recordEmail, offerRecord, alsoRecord }).any;

    const save = async () => {
        setTried(true); setError('');
        if (problem || unchanged) return;
        setBusy(true);
        try {
            const r = await onSave(value.trim(), offerRecord && alsoRecord);
            if (r && r.ok === false) setError(r.message || 'The address couldn’t be changed.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex flex-col gap-2 p-3 border border-[#5F4EC9]/30 bg-[#5F4EC9]/5 rounded-lg" role="group" aria-label="Change the email address">
            <label htmlFor={`${idPrefix}-email`} className="text-[13px] font-semibold text-gray-700">Email address</label>
            <input id={`${idPrefix}-email`} ref={inputRef} type="email" inputMode="email" autoComplete="off" value={value}
                onChange={(e) => { setValue(e.target.value); setError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); save(); } if (e.key === 'Escape') onCancel(); }}
                className={`${FIELD} ${tried && problem ? '!border-red-400' : ''}`} />
            {tried && problem && <span role="alert" className="text-xs text-red-600 font-semibold">{problem}</span>}
            {offerRecord ? (
                <label className="flex items-start gap-3 min-h-[44px] cursor-pointer">
                    <input type="checkbox" checked={alsoRecord} onChange={(e) => setAlsoRecord(e.target.checked)}
                        className="mt-1 h-5 w-5 rounded border-gray-300 text-[#5F4EC9] focus:ring-[#5F4EC9]" />
                    <span className="text-sm text-gray-800">Also update {customerName ? `${customerName}’s` : 'the customer’s'} record</span>
                </label>
            ) : (
                <p className="text-sm text-gray-700">{ESTIMATE_ONLY_NOTE}</p>
            )}
            <p className="text-xs text-gray-500">{OTHERS_KEEP_NOTE}</p>
            {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
            <div className="flex flex-wrap gap-2">
                <button type="button" onClick={save} disabled={busy || unchanged}
                    className="min-h-[44px] px-4 rounded-lg bg-[#5F4EC9] text-white font-semibold disabled:opacity-60 inline-flex items-center gap-2">
                    {busy && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}Save
                </button>
                <button type="button" onClick={onCancel} disabled={busy} className="min-h-[44px] px-4 rounded-lg border border-gray-300 bg-white font-semibold text-gray-700">Cancel</button>
            </div>
        </div>
    );
}
